import {
  parseTaskSectionSnapshot,
  type TaskSectionSnapshot,
} from '../section/dynamicSectionSnapshot';

type LayerResult = {
  id?: string;
  name?: string;
  kind?: string;
  totalCount?: number | null;
  stats?: Record<string, number | null>;
  groupCounts?: Record<string, Array<{ value?: string; count?: number }>>;
  dailyCounts?: Array<{ day?: string; count?: number }>;
  exemplars?: Array<Record<string, unknown>>;
  notes?: string[];
  queryErrors?: Array<{ query?: string; error?: string }>;
  assetExposure?: AssetExposure;
};

type AssetExposure = {
  radiusKm?: number;
  assetCount?: number;
  exposedCount?: number;
  rows?: Array<{
    name?: string;
    status?: 'inside' | 'within' | 'clear';
    distanceKm?: number | null;
    featureName?: string | null;
    value?: number | null;
    valueLabel?: string | null;
  }>;
};

type LayerPayload = {
  LayerQueryResults?: {
    window?: { label?: string | null };
    layers?: LayerResult[];
  } | null;
  notes?: string[];
  error?: string;
};

const STAT_LABELS: Record<string, string> = {
  max_wind: 'Highest wind',
  max_gust: 'Highest gust',
  max_surge: 'Highest storm surge',
  storm_count: 'Storms',
  acres_sum: 'Acres',
  population_exposed_sum: 'People exposed',
  mag_max: 'Largest magnitude',
  m5_plus_count: 'Magnitude 5 or greater',
  tsunami_count: 'Tsunami flagged',
  mmi_max: 'Highest shaking intensity (MMI)',
  mmi6_plus_count: 'Areas with strong shaking (MMI 6+)',
  frp_max: 'Highest fire power (MW)',
  frp_sum: 'Total fire power (MW)',
  high_confidence_count: 'High-confidence detections',
  force_max: 'Strongest gust class',
  pwind_max: 'Highest wind probability (%)',
  pm_aqi_max: 'Worst PM AQI',
  ozone_aqi_max: 'Worst ozone AQI',
  iav_detected_count: 'Influenza A detections',
  h5_detected_count: 'H5 detections',
  hpai_detected_count: 'High-path avian influenza detections',
  gust_max: 'Strongest gust (kt)',
  wind_max: 'Strongest wind (kt)',
  temp_max: 'Highest temperature (F)',
  temp_min: 'Lowest temperature (F)',
  heat_index_max: 'Highest heat index (F)',
  wind_chill_min: 'Lowest wind chill (F)',
  magnitude_max: 'Largest report',
  fatalities_sum: 'Fatalities',
  injuries_sum: 'Injuries',
  amount_max: 'Highest amount class',
  category_max: 'Highest outlook category',
  worst_rank: 'Worst winter impact',
};

const GROUP_LABELS: Record<string, string> = {
  STORMTYPE: 'Storm type',
  STORMNAME: 'Storm name',
  FCSTPRD: 'Forecast period',
  TCDVLP: 'Development',
  FireCause: 'Fire cause',
  fema_classification: 'Damage',
  alert: 'PAGER alert',
  confidence: 'Confidence',
  label: 'Gust class',
  PM_AQI_LABEL: 'PM AQI category',
  iav_detections_by_state: 'Influenza A detections by state',
  Final_IAV: 'Influenza A result',
  INCIDENT_TYPE: 'Report type',
  Interval: 'Forecast day',
  label2: 'Outlook category',
  outlook: 'Excessive rainfall outlook',
  impact: 'Winter storm impact',
  amount: 'Amount',
};

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function humanKey(key: string, labels: Record<string, string>): string {
  if (labels[key]) return labels[key];
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function exemplarName(row: Record<string, unknown>): string | null {
  if (typeof row.STATION_NAME === 'string' && row.STATION_NAME.trim()) {
    return row.STATION_NAME.trim();
  }
  if (typeof row.LOCATION === 'string' && row.LOCATION.trim()) {
    return [row.LOCATION.trim(), row.STATE].filter(Boolean).join(', ');
  }
  if (typeof row.County === 'string' && row.County.trim()) {
    return [row.County.trim(), row.State].filter(Boolean).join(', ');
  }
  const value =
    row.IncidentName ||
    row.STORMNAME ||
    row.COUNTY_NAME ||
    row.county ||
    row.name ||
    row.Name ||
    row.label ||
    row.Event ||
    row.place ||
    row.SiteName;
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function formatAssetExposure(block: AssetExposure): string | null {
  const rows = block.rows || [];
  if (typeof block.assetCount !== 'number') return null;
  const radius = formatCount(block.radiusKm ?? 0);
  const parts = [
    `<p>${escapeHtml(
      `Asset exposure: ${formatCount(block.exposedCount ?? 0)} of ${formatCount(block.assetCount)} assets inside or within ${radius} km.`
    )}</p>`,
  ];
  const exposed = rows.filter((row) => row.status && row.status !== 'clear');
  if (exposed.length > 0) {
    const items = exposed
      .slice(0, 25)
      .map((row) => {
        const where =
          row.status === 'inside'
            ? 'inside'
            : `${formatCount(row.distanceKm ?? 0)} km away`;
        const detail = [
          row.featureName,
          row.value != null ? formatCount(row.value) : null,
          row.valueLabel,
        ]
          .filter(Boolean)
          .join(', ');
        return `<li>${escapeHtml(
          `${row.name || 'Asset'}: ${where}${detail ? ` (${detail})` : ''}`
        )}</li>`;
      })
      .join('');
    parts.push(`<ul>${items}</ul>`);
  }
  return parts.join('');
}

function formatLayerBlock(layer: LayerResult): string {
  const title = layer.name?.trim() || 'Layer';
  const parts: string[] = [`<h4>${escapeHtml(title)}</h4>`];
  if (typeof layer.totalCount === 'number') {
    const noun = layer.totalCount === 1 ? 'feature' : 'features';
    parts.push(
      `<p>${formatCount(layer.totalCount)} ${noun} in this layer.</p>`
    );
  }
  const stats = Object.entries(layer.stats || {}).filter(
    ([, value]) => typeof value === 'number' && Number.isFinite(value)
  );
  if (stats.length > 0) {
    const sentence = stats
      .map(
        ([key, value]) =>
          `${humanKey(key, STAT_LABELS)}: ${formatCount(value as number)}`
      )
      .join('. ');
    parts.push(`<p>${escapeHtml(sentence)}.</p>`);
  }
  for (const [key, rows] of Object.entries(layer.groupCounts || {})) {
    if (!rows?.length) continue;
    const items = rows
      .slice(0, 15)
      .map(
        (row) => `${row.value ?? 'Unknown'} (${formatCount(row.count ?? 0)})`
      )
      .join(', ');
    parts.push(
      `<p>${escapeHtml(`${humanKey(key, GROUP_LABELS)}: ${items}.`)}</p>`
    );
  }
  if (layer.dailyCounts && layer.dailyCounts.length > 0) {
    const items = layer.dailyCounts
      .slice(0, 15)
      .map(
        (day) =>
          `<li>${escapeHtml(day.day)}: ${escapeHtml(formatCount(day.count ?? 0))}</li>`
      )
      .join('');
    parts.push(`<p>Daily counts</p><ul>${items}</ul>`);
  }
  const names = (layer.exemplars || [])
    .map(exemplarName)
    .filter((name): name is string => !!name)
    .slice(0, 12);
  if (names.length > 0) {
    parts.push(`<p>Examples: ${escapeHtml(names.join(', '))}.</p>`);
  }
  const exposure = layer.assetExposure
    ? formatAssetExposure(layer.assetExposure)
    : null;
  if (exposure) parts.push(exposure);
  if (layer.queryErrors && layer.queryErrors.length > 0) {
    parts.push('<p>Some statistics for this layer could not be loaded.</p>');
  }
  const notes = (layer.notes || []).map((note) => note.trim()).filter(Boolean);
  if (notes.length > 0) {
    parts.push(
      `<ul>${notes.map((note) => `<li>${escapeHtml(note)}</li>`).join('')}</ul>`
    );
  }
  return parts.join('');
}

function formatLayerPayload(payload: LayerPayload): string | null {
  if (payload.error) {
    return `<p>Analytics could not be loaded. ${escapeHtml(payload.error)}</p>`;
  }
  const results = payload.LayerQueryResults;
  if (!results?.layers?.length) return null;
  const parts: string[] = [];
  if (results.window?.label) {
    parts.push(`<p>Time window: ${escapeHtml(results.window.label)}.</p>`);
  }
  parts.push(...results.layers.map(formatLayerBlock));
  const notes = (payload.notes || [])
    .map((note) => note.trim())
    .filter(Boolean);
  if (notes.length > 0) {
    parts.push(
      `<ul>${notes.map((note) => `<li>${escapeHtml(note)}</li>`).join('')}</ul>`
    );
  }
  return parts.join('');
}

function formatTaskDate(value: unknown): string {
  if (!value) return '—';
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? '—' : date.toISOString().slice(0, 10);
}

function taskLocation(geolocation: unknown): string {
  if (!geolocation || typeof geolocation !== 'object') return '—';
  const row = geolocation as Record<string, unknown>;
  const name = row.name ?? row.address ?? row.label;
  return typeof name === 'string' && name.trim()
    ? name.trim()
    : 'Map location set';
}

const TASK_COLUMNS = [
  'ID',
  'Task',
  'Status',
  'Priority',
  'Assignee',
  'AOR',
  'Start',
  'Due',
  'Description',
  'Location',
  'Generated',
];

/**
 * Same columns as the server's formatTaskTableHtml, so the export matches the
 * approved section text.
 */
export function formatTaskSnapshotHtml(snapshot: TaskSectionSnapshot): string {
  if (snapshot.error) {
    return `<p><strong>Tasks error:</strong> ${escapeHtml(snapshot.error)}</p>`;
  }
  const sources = new Map(
    snapshot.links.map((link) => [link.action_id, link.source])
  );
  const blocks: Array<[string, Record<string, unknown>[]]> = [];
  if (snapshot.incident.length)
    blocks.push(['Incident tasks', snapshot.incident]);
  if (snapshot.org.length) blocks.push(['Org tasks', snapshot.org]);
  if (blocks.length === 0) return '<p>No tasks match this section.</p>';
  const header = `<thead><tr>${TASK_COLUMNS.map((column) => `<th>${column}</th>`).join('')}</tr></thead>`;
  return blocks
    .map(([title, rows]) => {
      const body = rows
        .map((task) => {
          const source = sources.get(String(task.action_id ?? ''));
          const cells = [
            escapeHtml(task.display_id ?? '—'),
            escapeHtml(task.name ?? 'Untitled task'),
            escapeHtml(task.status ?? '—'),
            escapeHtml(task.priority ?? '—'),
            escapeHtml(task.poc_user_name ?? task.assignee_name ?? '—'),
            escapeHtml(task.aor_name ?? '—'),
            formatTaskDate(task.start_date),
            formatTaskDate(task.target_date),
            escapeHtml(task.description || '—'),
            escapeHtml(taskLocation(task.geolocation)),
            source === 'generated'
              ? 'Generated'
              : source === 'pinned'
                ? 'Pinned'
                : '',
          ];
          return `<tr>${cells.map((cell) => `<td>${cell}</td>`).join('')}</tr>`;
        })
        .join('');
      const heading =
        blocks.length > 1 ? `<p><strong>${title}</strong></p>` : '';
      return `${heading}<p>${rows.length} ${rows.length === 1 ? 'task' : 'tasks'}</p><table>${header}<tbody>${body}</tbody></table>`;
    })
    .join('');
}

function layerHtmlFromDataString(
  raw: string | null | undefined
): string | null {
  if (!raw || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!('LayerQueryResults' in parsed) && !parsed.error && !parsed.notes) {
      return null;
    }
    return formatLayerPayload(parsed as LayerPayload);
  } catch {
    return null;
  }
}

/**
 * Export body for one section. Analytics becomes sentences and lists. Other
 * sections keep their saved HTML so paragraphs and tables survive. A Tasks
 * section exports its saved task table, after the intro text when the table is
 * not already part of the text.
 */
export function readableSectionHtml(section: {
  sitrep_section_text?: string | null;
  data_string?: string | null;
}): string {
  const text = section.sitrep_section_text?.trim() || '';
  const tasks = parseTaskSectionSnapshot(section.data_string);
  if (tasks) {
    if (tasks.tableInText && text) return text;
    return `${text}${formatTaskSnapshotHtml(tasks)}`;
  }
  const layers = layerHtmlFromDataString(section.data_string);
  if (layers) return layers;
  if (text) return text;
  return '<p>No content in this section.</p>';
}
